/**
 * Fixed banner shown while an admin is signed in as a customer.
 *
 * Mounted at the App root (not inside MainLayout) so it is visible on every
 * screen, including public/standalone layouts. Renders nothing unless a
 * sessionStorage hint exists; the hint is then reconciled against
 * /api/my-profile/ (the server-authoritative state) so a stale hint can never
 * strand a fake banner, and a real session can never lose its banner.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import UseAPI from '../../lib/api/UseAPI';
import api_config from '../../lib/api/common';
import {
    readImpersonationHint,
    writeImpersonationHint,
    clearImpersonationHint,
    exitImpersonation,
} from '../../lib/impersonation';

const BAR_HEIGHT = 36;
// Exit before the token actually expires: a 401 would wipe client storage and
// cost the admin their own session state too.
const AUTO_EXIT_LEAD_MS = 30 * 1000;

const barStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: BAR_HEIGHT,
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    background: '#7c2d12', // deliberately outside the product palette
    color: '#fff',
    fontSize: 13,
    fontWeight: 500,
    padding: '0 12px',
};

const exitBtnStyle = {
    background: '#fff',
    color: '#7c2d12',
    border: 'none',
    borderRadius: 4,
    padding: '2px 10px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
};

const formatRemaining = (ms) => {
    if (ms == null) return '';
    const total = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
};

const ImpersonationBanner = () => {
    const [hint, setHint] = useState(() => readImpersonationHint());
    const [remainingMs, setRemainingMs] = useState(null);
    const [exiting, setExiting] = useState(false);
    const exitingRef = useRef(false);

    const doExit = useCallback(async () => {
        if (exitingRef.current) return;
        exitingRef.current = true;
        setExiting(true);
        await exitImpersonation();
    }, []);

    // Reconcile the hint against the server exactly once, and only when a
    // hint exists — an unconditional call would add a request per page load
    // for every ordinary user.
    useEffect(() => {
        if (!hint) return;
        let cancelled = false;
        (async () => {
            try {
                const result = await UseAPI(null, `${api_config.BASE_BACKEND_ENDPOINT}api/my-profile/`, 'GET', null);
                if (cancelled) return;
                const imp = result && result.user_info && result.user_info.impersonation;
                if (imp && imp.active) {
                    const merged = {
                        ...hint,
                        admin_email: imp.admin_email || hint.admin_email,
                        admin_name: imp.admin_name || hint.admin_name,
                        expires_at: imp.expires_at || hint.expires_at,
                        target_email: (result.user_info && result.user_info.email) || hint.target_email,
                    };
                    writeImpersonationHint(merged);
                    setHint(merged);
                } else if (result && result.user_info) {
                    // Server says this session is NOT impersonated — stale hint.
                    clearImpersonationHint();
                    setHint(null);
                }
            } catch (e) { /* keep hint; next load reconciles */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Countdown + auto-exit.
    useEffect(() => {
        if (!hint || !hint.expires_at) return undefined;
        const expiresAt = new Date(hint.expires_at).getTime();
        if (Number.isNaN(expiresAt)) return undefined;
        const tick = () => {
            const left = expiresAt - Date.now();
            setRemainingMs(left);
            if (left <= AUTO_EXIT_LEAD_MS) {
                doExit();
            }
        };
        tick();
        const timer = setInterval(tick, 1000);
        return () => clearInterval(timer);
    }, [hint, doExit]);

    // Push the app down so the bar never covers the header.
    useEffect(() => {
        if (!hint) return undefined;
        const previous = document.body.style.paddingTop;
        document.body.style.paddingTop = `${BAR_HEIGHT}px`;
        return () => { document.body.style.paddingTop = previous; };
    }, [hint]);

    if (!hint) return null;

    return (
        <div style={barStyle}>
            <span role="img" aria-label="warning">⚠️</span>
            <span>
                Viewing as <strong>{hint.target_email || 'customer'}</strong>
                {hint.admin_email ? ` — admin ${hint.admin_email}` : ''}
                {remainingMs != null ? ` — ${formatRemaining(remainingMs)} left` : ''}
            </span>
            <button type="button" style={exitBtnStyle} onClick={doExit} disabled={exiting}>
                {exiting ? 'Exiting…' : 'Exit session'}
            </button>
        </div>
    );
};

export default ImpersonationBanner;
